// Spellcaster eligibility helpers (v1.14). Pure. No React, no
// network. GameBoard wraps these with the casting flow, caster-pick
// prompt, and ledger-routed spell disposal.
//
// Sorcery rules:
//   • Any controlled Avatar is a Spellcaster.
//   • Any controlled Minion with the Spellcaster keyword qualifies.
//     printed (in rules text), aura-granted, or manually-set.
//   • Disabled units lose all keywords and can't cast.
//   • An element-specific Spellcaster ("Fire Spellcaster") may only
//     cast spells requiring that element's threshold.

import { isDisabled } from '../scope';
import { countsAsMinion } from '../../core/typeline';
import { isCardSilenced } from '../../core/keywordQuery';

const SPELLCASTER = 'spellcaster';
const ELEMENTS = ['air', 'earth', 'fire', 'water'];

/**
 * True if this card can currently cast spells. Catalog `fullCard`
 * is optional. Used to recognise Avatars; if absent, only the
 * cardInstance.type field is consulted.
 */
export function isSpellcaster(cardInstance, fullCard) {
  if (!cardInstance) return false;
  if (isDisabled(cardInstance)) return false;
  const type = fullCard?.type || cardInstance.type;
  // Avatars are ALWAYS spellcasters. A core rule. They cast any spell
  // (magic, minion, otherwise) regardless of element, and Silence does NOT
  // strip it (it's not box text). Only Disable (no abilities at all) stops
  // an Avatar from casting.
  if (type === 'Avatar') return true;
  // Silence removes a minion's keywords. Including a printed / aura-granted
  // Spellcaster keyword. So a silenced minion can no longer cast.
  if (isCardSilenced(cardInstance)) return false;
  if (!countsAsMinion(fullCard || cardInstance)) return false;
  if ((cardInstance._printedKeywords || []).includes(SPELLCASTER)) return true;
  if ((cardInstance._auraStatuses    || []).includes(SPELLCASTER)) return true;
  if ((cardInstance.statuses          || []).includes(SPELLCASTER)) return true;
  return false;
}

/**
 * The element restriction a Spellcaster casts under, parsed from its
 * keyword line ("Fire Spellcaster", "Earth and Water Spellcaster").
 * Returns null for a generic Spellcaster, an Avatar, or a non-caster.
 * null means no restriction (may cast any spell). Only consulted for
 * cards already known to be Spellcasters, so an effect-text mention of
 * "Spellcaster" on a non-caster never reaches it.
 */
export function spellcasterElements(fullCard) {
  // The normalized catalog card (what _getFullCard returns) exposes the rules
  // text as `functional_text`; raw / unit-test cards use `rulesText`. Read both
  // so the element line ("Earth and Water Spellcaster") is found at runtime,
  // not just in tests.
  const text = fullCard?.rulesText || fullCard?.functional_text || fullCard?.functional_text_plain || '';
  const m = text.match(/((?:air|earth|fire|water)(?:\s+and\s+(?:air|earth|fire|water))*)\s+spellcaster/i);
  if (!m) return null;
  return new Set(m[1].toLowerCase().match(/air|earth|fire|water/g));
}

/**
 * The {air,earth,fire,water} threshold a spell requires, read from a
 * catalog card. Null when the card is absent.
 */
export function cardThresholds(fullCard) {
  if (!fullCard) return null;
  const out = {};
  for (const e of ELEMENTS) {
    out[e] = fullCard[`${e}Threshold`] || fullCard.thresholds?.[e] || 0;
  }
  return out;
}

/**
 * True if a caster restricted to `elements` (a Set, or null for
 * unrestricted) may cast a spell requiring `spellThresholds` (an
 * {air,earth,fire,water} map, or null for no requirement). Codex: an
 * element spellcaster "may only cast spells that require [its]
 * threshold", so the spell must require at least one of its elements.
 */
export function elementAllowsCast(elements, spellThresholds) {
  if (!elements) return true;
  if (!spellThresholds) return false;
  for (const e of elements) if ((spellThresholds[e] || 0) > 0) return true;
  return false;
}

/**
 * True if an owner-locked spell's predetermined caster (`ownerFullCard`)
 * is allowed to cast that spell under its element restriction. The four
 * Omphalos are element-restricted ("Earth and Water Spellcaster") and may
 * only cast a held spell requiring one of their elements; a full/generic
 * spellcaster owner (Morgana le Fay) has no element line, so this is
 * unrestricted. Composes the parse + threshold-read + gate helpers.
 */
export function ownerElementAllowsCast(ownerFullCard, spellFullCard) {
  return elementAllowsCast(spellcasterElements(ownerFullCard), cardThresholds(spellFullCard));
}

/**
 * Find every valid Spellcaster controlled by `controller` on the
 * current board. Returns cardInstance objects. When `spellThresholds`
 * is supplied (the spell being cast), element-specific spellcasters
 * are filtered to those allowed to cast it; pass null to list every
 * spellcaster regardless of element.
 *
 * @param {'p1'|'p2'} controller
 * @param {Iterable<[any, { userData: { cardInstance } }]>} meshes
 * @param {(cardId: string) => Object|null} getFullCard - catalog lookup
 * @param {{air,earth,fire,water}|null} spellThresholds
 */
export function findValidSpellcasters(controller, meshes, getFullCard, spellThresholds = null) {
  const wantRotated = controller === 'p2';
  const results = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.rotated !== wantRotated) continue;
    const fc = getFullCard(card.cardId);
    if (!isSpellcaster(card, fc)) continue;
    // Wiccan Tools waives this bearer's threshold requirement this turn:
    // it may cast any spell regardless of element, so the element-match
    // gate is skipped for it (the waiver is per-bearer, never global).
    // Two waiver shapes: the legacy local grant (_thresholdWaiverThisTurn)
    // and the server-folded field (_spellcasterNoThresholdThisTurn).
    if (spellThresholds && !card._thresholdWaiverThisTurn
        && !card._spellcasterNoThresholdThisTurn
        && !elementAllowsCast(spellcasterElements(fc), spellThresholds)) continue;
    results.push(card);
  }
  return results;
}
